import React, { useState, useContext, useEffect, useRef } from "react";
import axios from "axios";
import "../pagesStyling/AddDataCollection.css";
import Divider from "../components/Divider/Divider";
import Button from "../components/Button/Button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../components/AuthContext/AuthContext";
import { API_BASE_URL } from "../apiConfig";

interface Collection {
  uuid: string;
  name: string;
  source_origin: string;
  source_url: string;
  parent_id: string | null;
  last_sync_at: string;
  public_collection: boolean;
  restricted: boolean;
  has_read_access: boolean;
  has_write_access: boolean;
  access_requested: boolean;
  created_by: string;
  admin_data: {
    restricted_to?: string[];
    pending_access?: string[];
    access_denied?: string[];
  };
}

interface SyncProgress {
  status: "Started" | "Progress" | "Completed";
  processed?: number;
  total?: number;
}

function AddDataCollection() {
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState("Google Drive");
  const [sourceUrl, setSourceUrl] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [isRestricted, setIsRestricted] = useState(false);
  const [owner, setOwner] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [restrictedTo, setRestrictedTo] = useState<string[]>([]);
  const [pendingAccess, setPendingAccess] = useState<string[]>([]);
  const [accessDenied, setAccessDenied] = useState<string[]>([]);
  const [initialRestrictedTo, setInitialRestrictedTo] = useState<string[]>([]);
  const [initialPendingAccess, setInitialPendingAccess] = useState<string[]>(
    []
  );
  const [initialAccessDenied, setInitialAccessDenied] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [newCollectionId, setNewCollectionId] = useState<string | null>(null);
  const { token } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const location = useLocation();
  const collection = location.state?.collection as Collection | undefined;
  const isEditMode = !!collection;
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!token) {
      setErrorMessage("You are not authenticated. Redirecting to login...");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (isEditMode && collection) {
      setName(collection.name || "");
      setOwner(collection.created_by || "");
      const sourceOriginMap: { [key: string]: string } = {
        GDRIVE: "Google Drive",
        CONFLUENCE: "Confluence",
        JIRA: "Jira",
        WINDCHILL: "Windchill",
        LOCAL: "Local",
      };
      setSourceType(
        sourceOriginMap[collection.source_origin] || "Google Drive"
      );
      setSourceUrl(collection.source_url || "");
      setIsPublic(collection.public_collection || false);
      setIsRestricted(
        collection.public_collection ? collection.restricted : false
      );
      setInitialRestrictedTo([...(collection.admin_data?.restricted_to || [])]);
      setInitialPendingAccess([
        ...(collection.admin_data?.pending_access || []),
      ]);
      setInitialAccessDenied([...(collection.admin_data?.access_denied || [])]);
      setRestrictedTo([...(collection.admin_data?.restricted_to || [])]);
      setPendingAccess([...(collection.admin_data?.pending_access || [])]);
      setAccessDenied([...(collection.admin_data?.access_denied || [])]);
    }
  }, [collection, isEditMode]);

  useEffect(() => {
    if (!isPublic) {
      setIsRestricted(false);
    }
  }, [isPublic]);

  useEffect(() => {
    if (newCollectionId && token) {
      const wsProtocol = "wss";
      const baseWsUrl = API_BASE_URL.replace(/^http/, wsProtocol).replace(
        /\/+$/,
        ""
      );
      const wsUrl = `${baseWsUrl}/ws/sync/${newCollectionId}/`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {};

      wsRef.current.onerror = (error) => {
        setSyncProgress(null);
        setSuccessMessage("WebSocket error occurred during embedding.");
        setTimeout(() => setSuccessMessage(null), 3000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          setSyncProgress(payload);

          if (payload.status === "Completed") {
            setSuccessMessage("Embedding process completed. You can now chat!");
            setTimeout(() => {
              setSuccessMessage(null);
              setSyncProgress(null);
              wsRef.current?.close();
              navigate("/", { state: { newCollectionId } });
            }, 3000);
          }
        } catch (e) {}
      };

      wsRef.current.onclose = () => {
        setSyncProgress(null);
        if (!successMessage) {
          setSuccessMessage(
            "Embedding connection closed. Process may not be complete."
          );
          setTimeout(() => setSuccessMessage(null), 3000);
        }
      };

      return () => {
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [newCollectionId, token]);

  const getSourceOrigin = (sourceType: string): string => {
    const sourceTypeMap: { [key: string]: string } = {
      "Google Drive": "GDRIVE",
      Confluence: "CONFLUENCE",
      Jira: "JIRA",
      Windchill: "WINDCHILL",
      Local: "LOCAL",
    };
    return sourceTypeMap[sourceType] || "GDRIVE";
  };

  const handleAcceptUser = (user: string) => {
    setPendingAccess((prev) => prev.filter((u) => u !== user));
    setRestrictedTo((prev) => [...prev, user]);
  };

  const handleDeclineUser = (user: string) => {
    setPendingAccess((prev) => prev.filter((u) => u !== user));
    setAccessDenied((prev) => [...prev, user]);
  };

  const handleRemoveUser = (user: string) => {
    setRestrictedTo((prev) => prev.filter((u) => u !== user));
  };

  const handleRemoveUserFromDenied = (user: string) => {
    setAccessDenied((prev) => prev.filter((u) => u !== user));
  };

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSaveDataCollection = async () => {
    let errorMsg = "";

    if (!name) {
      errorMsg = "Name is required.";
    } else if (!sourceUrl) {
      errorMsg = "Source URL is required.";
    } else if (!validateUrl(sourceUrl)) {
      errorMsg = "Please enter a valid URL.";
    } else if (isEditMode && !collection?.uuid) {
      errorMsg = "Invalid collection UUID.";
    } else if (!token) {
      errorMsg = "Authentication token is missing.";
    } else if (!isPublic && isRestricted) {
      errorMsg = "A private collection cannot be restricted.";
    }

    if (errorMsg) {
      setErrorMessage(errorMsg);
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return;
    }

    try {
      const endpoint = `${API_BASE_URL}/collections/`;

      if (isEditMode) {
        const payload = {
          uuid: collection!.uuid,
          name,
          public: isPublic,
          restricted: isRestricted,
          restricted_to: restrictedTo,
          pending_access: pendingAccess,
          access_denied: accessDenied,
          owner: owner || collection!.created_by || "",
        };

        const response = await axios.put(endpoint, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });

        setSuccessMessage(
          response.data?.message || "Data collection updated successfully!"
        );
        navigate("/");
      } else {
        const payload = {
          name,
          source_origin: getSourceOrigin(sourceType),
          source_url: sourceUrl,
          public: isPublic,
          restricted: isRestricted,
          restricted_to: restrictedTo,
          parent_id: null,
          ...(owner && { owner }),
        };

        const response = await axios.post(endpoint, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });

        setNewCollectionId(response.data.uuid);
        setSuccessMessage(
          "Data collection created. Embedding process started..."
        );
        setTimeout(() => {
          navigate("/");
        }, 1000);
      }
    } catch (error: any) {
      let errorMsg = "Failed to save data collection.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Authentication failed. Please log in again.";
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else if (error.response.status === 404) {
          errorMsg = "API endpoint not found. Please contact support.";
        } else if (error.response.status === 422) {
          const errorDetails = error.response.data.detail;
          if (Array.isArray(errorDetails)) {
            errorMsg =
              errorDetails.map((err: any) => err.msg).join("; ") ||
              "Invalid data provided.";
          } else {
            errorMsg = error.response.data.message || "Invalid data provided.";
          }
        } else if (error.response.status === 403) {
          errorMsg = "You do not have permission to perform this action.";
        } else {
          errorMsg = error.response.data?.message;
        }
      } else {
        errorMsg = "Network error. Please check your connection.";
      }
      setErrorMessage(errorMsg);
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
    }
  };

  return (
    <div className="add-data-collection-container">
      <div className="add-data-collection-form-container">
        <div className="add-data-collection__comeback-link">
          <Link to="/" className="add-data-collection__breadcrumb-link">
            Home
          </Link>
          <label>/</label>
          <label>
            {isEditMode ? "Edit data collection" : "Add data collection"}
          </label>
        </div>
        <h1 className="add-data-collection-title">
          {isEditMode ? "Edit data collection" : "Add new data collection"}
        </h1>
        {!isEditMode && (
          <p className="instructional-message">
            Fill out the form below to create a new data collection.
          </p>
        )}
        <div className="add-data-collection-field">
          <label className="add-data-collection-label">Name *</label>
          <input
            className="add-data-collection-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="add-data-collection-field">
          <label className="add-data-collection-label">Owner</label>
          <input
            className="add-data-collection-input"
            type="text"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Enter owner email or username"
          />
        </div>
        <div className="add-data-collection-row">
          <div className="add-data-collection-field">
            <label className="add-data-collection-label">Source type *</label>
            <select
              className="add-data-collection-select"
              value={sourceType}
              onChange={(e) => {
                if (!isEditMode && e.target.value === "Google Drive") {
                  setSourceType(e.target.value);
                }
              }}
              disabled={isEditMode}
            >
              <option value="Google Drive">Google Drive</option>
              <option value="Confluence" disabled>
                Confluence (Coming soon)
              </option>
              <option value="Jira" disabled>
                Jira (Not Available)
              </option>
              <option value="Windchill" disabled>
                Windchill (Not Available)
              </option>
              <option value="Local" disabled>
                Local (Not Available)
              </option>
            </select>
          </div>
          <div className="add-data-collection-field">
            <label className="add-data-collection-label">Source URL *</label>
            <input
              className="add-data-collection-input"
              type="text"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              required
              disabled={isEditMode}
            />
          </div>
        </div>
        <div className="add-data-collection-field add-data-collection-visibility">
          <label className="add-data-collection-label">Visibility *</label>
          <select
            value={isPublic ? "Public" : "Private"}
            onChange={(e) => setIsPublic(e.target.value === "Public")}
            className="add-data-collection-select"
          >
            <option value="Public">Public</option>
            <option value="Private">Private</option>
          </select>
        </div>
        <div className="add-data-collection-field add-data-collection-visibility">
          <label className="add-data-collection-label">Restricted *</label>
          <select
            value={isRestricted ? "Restricted" : "Unrestricted"}
            onChange={(e) => setIsRestricted(e.target.value === "Restricted")}
            className="add-data-collection-select"
            disabled={!isPublic}
          >
            <option value="Unrestricted">Unrestricted</option>
            <option value="Restricted" disabled={!isPublic}>
              Restricted
            </option>
          </select>
        </div>
        {!isPublic && (
          <p className="add-data-collection-note">
            Restricted access is only available for public collections.
          </p>
        )}
        {isEditMode && (
          <>
            <Divider />
            <h2 className="add-data-collection-title">Access</h2>
            <div className="add-data-collection-access-section">
              <div className="add-data-collection-access-section-container">
                <h3 className="add-data-collection-subtitle">Access granted</h3>
                <div className="add-data-collection-user-groups">
                  {restrictedTo.length > 0 ? (
                    restrictedTo.map((user) => (
                      <div key={user} className="add-data-collection-user-item">
                        <span>{user}</span>
                        <button
                          className="add-data-collection__action-btn"
                          onClick={() => handleRemoveUser(user)}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <p>No users with restricted access.</p>
                  )}
                </div>
                <Divider />
                <h3 className="add-data-collection-subtitle">Pending Access</h3>
                <div className="add-data-collection-user-groups">
                  {pendingAccess.length > 0 ? (
                    pendingAccess.map((user) => (
                      <div key={user} className="add-data-collection-user-item">
                        <span>{user}</span>
                        <div>
                          <button
                            className="add-data-collection__action-btn"
                            onClick={() => handleAcceptUser(user)}
                          >
                            Accept
                          </button>
                          <button
                            className="add-data-collection__action-btn"
                            onClick={() => handleDeclineUser(user)}
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No users with pending access.</p>
                  )}
                </div>
                <Divider />
                <h3 className="add-data-collection-subtitle">Access Denied</h3>
                <div className="add-data-collection-user-groups">
                  {accessDenied.length > 0 ? (
                    accessDenied.map((user) => (
                      <div key={user} className="add-data-collection-user-item">
                        <span>{user}</span>
                        <button
                          className="add-data-collection__action-btn"
                          onClick={() => handleRemoveUserFromDenied(user)}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <p>No users with access denied.</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        <Divider />
        <div className="add-data-collection-buttons">
          <Button
            text="Cancel"
            onClick={() => navigate("/")}
            variant="secondary"
          />
          <Button
            text={isEditMode ? "Save changes" : "Create data collection"}
            onClick={handleSaveDataCollection}
          />
        </div>
      </div>
      <div className="add-data-collection-warning">
        <div className="search-result-findings">
          <div className="search-result-finding-text">IMPORTANT</div>
          <div className="search-result-finding-page">
            To ensure that the data can sync properly, please provide
            aiplatform@roche.com with READ access to the data source’s access
            list. If this step is not completed the system will be unable to
            retrieve or sync the required data.
          </div>
        </div>
      </div>
      {errorMessage && (
        <div className="add-data-collection-error">{errorMessage}</div>
      )}
      {successMessage && (
        <div className="add-data-collection-success">{successMessage}</div>
      )}
      {syncProgress && !isEditMode && (
        <div className="sync-progress-container">
          <h3>Embedding Process</h3>
          <p>
            Embedding is in progress. You cannot chat until it’s complete.
            Status: {syncProgress.status}
          </p>
          {syncProgress.status === "Progress" &&
            syncProgress.processed !== undefined &&
            syncProgress.total !== undefined && (
              <div className="progress-bar">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${
                      (syncProgress.processed / syncProgress.total) * 100
                    }%`,
                  }}
                />
                <span className="progress-bar-text">
                  {syncProgress.processed} / {syncProgress.total}
                </span>
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export default AddDataCollection;
